import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { auctionPropertiesType, auctionType as auctionTypeRef } from '../../types';
import ReadonlyField from '../fields/ReadonlyField';
import './_styles_/AuctionDetailTab.css';
import { CANCELLED, CLOSED, DELETED, FINISHED, getUIPhase, PUBLISHED, RUNNING, SUBMITTED } from '../../utils/AuctionPhase';
import { useExtendedState } from '../../hooks/useExtendedState';
import useMultiValidator from '../../hooks/useMultiValidator';
import {
    AUCTION_DESCRIPTION_MAX_LENGTH,
    AUCTION_NAME_LENGTH,
    auctionAbsoluteDeviationValidator,
    auctionAuctioneerDataItemsValidator,
    auctionDateValidator,
    auctionDeliveryYearValidator,
    auctionDescriptionValidator,
    auctionMaxBidSizeValidator,
    auctionMaxPriceValidator,
    auctionMinPriceValidator,
    auctionNameValidator,
    auctionPowerValidator,
    auctionQuantityValueValidator,
    auctionRelativeDeviationValidator,
    auctionRevenueSplitValidator,
    auctionSmallestQuantityUnitValidator,
    auctionSpotReferencePriceValidator,
} from '../../validators/auctions';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import {
    ACCEPT_AUCTION,
    ALL_AUCTIONS_QUERY_NAME,
    AUCTION_DETAIL,
    CANCEL_AUCTION,
    CLOSE_AUCTION,
    OPEN_AUCTION,
    PUBLISH_AUCTION,
    REJECT_AUCTION,
    UPDATE_AUCTION,
} from '../../graphql/auctions';
import { date, getCurrentYear, onActionKey } from '../../utils';
import Constants from '../../utils/Constants';
import Privileges from '../../utils/Privileges';
import PrivilegedTextField from '../fields/privileged/PrivilegedTextField';
import PrivilegedNumberField from '../fields/privileged/PrivilegedNumberField';
import PrivilegedSelectField from '../fields/privileged/PrivilegedSelectField';
import { hasPrivilege, Privileged } from '../../hocs/withPrivileges';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import PrivilegedCheckboxField from '../fields/privileged/PrivilegedCheckboxField';
import PrivilegedDateTimeField from '../fields/privileged/PrivilegedDateTimeField';
import Unit from '../Unit';
import PrivilegedAuctionEnergyPerLot from './energy/PrivilegedAuctionEnergyPerLot';
import AuctionTransitionButton from './AuctionTransitionButton';
import AuctionResult from './AuctionResult';
import AuctionReportsMenu from '../reports/AuctionReportsMenu';
import useAuthentication from '../../hooks/useAuthentication';
import ShowBidsPopupButton from './ShowBidsPopupButton';
import ReadonlyNumberField from '../fields/ReadonlyNumberField';
import ExpandedListTab from '../ExpandedListTab';
import { BID_PRICE, getTradePriceCalculationType } from '../../utils/TradePriceCalculationTypes';
import { getBiddingType, OPEN, SLOTTED } from '../../utils/BiddingTypes';
import { BUY, getAuctionType, SELL } from '../../utils/AuctionTypes';
import { getAuctionAlgorithm } from '../../utils/AuctionAlgorithms';
import { AUTO, getAuctionAcceptanceType } from '../../utils/AuctionAcceptanceTypes';
import { getSettlementStartType } from '../../utils/SettlementStartTypes';
import { ENERGY, getProductType, SECURITY } from '../../utils/ProductTypes';
import { getScope } from '../../utils/Scopes';
import InfoPanel from '../InfoPanel';
import { AUCTION_BLOCKER_MODIFIED, transformAuctionBlockers } from '../blockers/AuctionBlockers';
import AuctioneerDataMatrix from '../auctioneer-data-matrix/AuctioneerDataMatrix';
import useSingleValidator from '../../hooks/useSingleValidator';
import AuctionReopenButton from './AuctionReopenButton';
import AuctionDistributeVolumeButton from './AuctionDistributeVolumeButton';
import { computeHighestAcceptablePrice, computeLowestAcceptablePrice } from '../../utils/auctions';
import Collapsible from '../Collapsible';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

AuctionDetailTab.propTypes = {
    auction: auctionTypeRef.isRequired,
    auctionProps: auctionPropertiesType.isRequired,
};

function AuctionDetailTab(props) { // NOSONAR javascript:S3776
    const {
        auction,
        auctionProps,
    } = props;
    const {
        availableProducts,
        availableZones,
        availableAuctioneers,
        availableAuctionTypes,
        availableBiddingTypes,
        availableTradePriceCalculationTypes,
        availableAuctionAlgorithms,
        availableAuctionAcceptanceTypes,
        availableSubmarkets,
        availableAuctioneerDataTemplates,
        availableSettlStartTypes,
        volumeDistributionStep,
    } = auctionProps;
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const availableRegularAuctioneers = availableAuctioneers.filter((availableAuctioneer) => availableAuctioneer.regularAuctioneer);
    const availableTechnicalAuctioneers = availableAuctioneers.filter((availableAuctioneer) => availableAuctioneer.technicalAuctioneer);
    const currencyCode = auction.zone.currency.code;
    const currencyPostfix = <>&nbsp;{currencyCode}</>;
    const [resetComponentsAfterModification, setResetComponentsAfterModification] = useState(false);
    const nameState = useExtendedState(auction.name);
    const deliveryYearState = useExtendedState(auction.deliveryYear || getCurrentYear().toString());
    const productState = useExtendedState(auction.product);
    const productTypeState = useExtendedState(getProductType(auction.product.productType));
    const zoneState = useExtendedState(auction.zone);
    const auctioneerState = useExtendedState(auction.auctioneerMember);
    const quantityValueState = useExtendedState(auction.quantity);
    const powerState = useExtendedState(auction.power || 1);
    const openingTimeState = useExtendedState(date(auction.openingTime));
    const closingTimeState = useExtendedState(date(auction.closureTime));
    useEffect(() => {
        const openingTime = date(auction.openingTime);
        const closureTime = date(auction.closureTime);
        if (openingTimeState.initialValue.getTime() !== openingTime.getTime()) {
            openingTimeState.changeInitialState(openingTime);
            openingTimeState.reset();
        }
        if (closingTimeState.initialValue.getTime() !== closureTime.getTime()) {
            closingTimeState.changeInitialState(closureTime);
            closingTimeState.reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        auction.openingTime,
        openingTimeState.changeInitialState,
        openingTimeState.reset,
        auction.closureTime,
        closingTimeState.changeInitialState,
        closingTimeState.reset,
    ]);
    const descriptionState = useExtendedState(auction.description);
    const minPriceState = useExtendedState(auction.minPrice);
    const maxPriceState = useExtendedState(auction.maxPrice);
    const spotReferencePriceState = useExtendedState(auction.spotReferencePrice);
    const absoluteDeviationState = useExtendedState(auction.absoluteDeviation);
    const relativeDeviationState = useExtendedState(auction.relativeDeviation);
    const showLastPriceState = useExtendedState(auction.showLastPrice || false);
    const showRankingState = useExtendedState(auction.showRanking || false);
    const limitBidModificationState = useExtendedState(auction.limitBidModification || false);
    const auctionTypeState = useExtendedState(auction.auctionType);
    const biddingTypeState = useExtendedState(getBiddingType(auction.biddingType));
    const tradePriceCalculationTypeState = useExtendedState(getTradePriceCalculationType(auction.tradePriceCalculationType));
    const acceptanceTypeState = useExtendedState(auction.auctionAcceptanceType);
    const algorithmState = useExtendedState(auction.algorithm);
    const smallestQuantityUnitState = useExtendedState(auction.smallestUnit || Constants.MIN_SMALLEST_UNIT);
    const maxBidSizeState = useExtendedState(auction.maxBidSize || null);
    const settlementStartTypeState = useExtendedState(auction.settlStartType || availableSettlStartTypes[0]);
    const fastMarketState = useExtendedState(auction.fastMarket || false);
    const auctioneerDataItemsState = useExtendedState(auction.auctioneerDataItems ? [...auction.auctioneerDataItems] : []);
    const [isPublishingDisabled, setPublishingDisabled] = useState(closingTimeState.value < date());
    const roundUpHighestAcceptablePriceState = useExtendedState(auction.roundUpHighestAcceptablePrice);
    const { quantityUnit } = productState.value;
    const [isShowConfirmation, setIsShowConfirmation] = useState(false);
    const revenueSplitState = useExtendedState(auction.revenueSplit);
    const onItemsChange = (newItems) => {
        setResetComponentsAfterModification(false);
        auctioneerDataItemsState.setValue(newItems);
    };
    const [debouncedDeliveryYear] = useDebounce(deliveryYearState.value, Constants.ENERGY_PER_LOT_PROPERTY_DEBOUNCE_IN_MS);
    const [debouncedPower] = useDebounce(powerState.value, Constants.ENERGY_PER_LOT_PROPERTY_DEBOUNCE_IN_MS);
    const { validators, isValid } = useMultiValidator(true, {
        name: compositeValidator(auctionNameValidator(t), wafValidator(t)),
        deliveryYear: auctionDeliveryYearValidator(t),
        quantityValue: compositeValidator(auctionQuantityValueValidator(t, productTypeState.value), wafValidator(t)),
        power: compositeValidator(auctionPowerValidator(t), wafValidator(t)),
        openingTime: auctionDateValidator(t),
        closingTime: auctionDateValidator(t),
        description: compositeValidator(auctionDescriptionValidator(t), wafValidator(t)),
        spotReferencePrice: auctionSpotReferencePriceValidator(t, Constants.MIN_SPOT_REFERENCE_PRICE, Constants.MAX_SPOT_REFERENCE_PRICE),
        absoluteDeviation: auctionAbsoluteDeviationValidator(t, Constants.MIN_ABSOLUTE_DEVIATION, Constants.MAX_ABSOLUTE_DEVIATION),
        relativeDeviation: auctionRelativeDeviationValidator(t, Constants.MIN_RELATIVE_DEVIATION, Constants.MAX_RELATIVE_DEVIATION),
        revenueSplit: auctionRevenueSplitValidator(t, Constants.MIN_REVENUE_SPLIT, Constants.MAX_REVENUE_SPLIT),
    });
    const { validator: minPriceValidator, isValid: isMinPriceValid } = useSingleValidator(true, useMemo(() => compositeValidator(auctionMinPriceValidator(t, maxPriceState.value), wafValidator(t)), [t, maxPriceState.value]));
    const { validator: maxPriceValidator, isValid: isMaxPriceValid } = useSingleValidator(true, useMemo(() => compositeValidator(auctionMaxPriceValidator(t, minPriceState.value), wafValidator(t)), [t, minPriceState.value]));
    const {
        validator: smallestQuantityUnitValidator,
        isValid: isSmallestQuantityUnitValid,
        reset: resetSmallestQuantityUnitValidator,
    } = useSingleValidator(true, useMemo(() => compositeValidator(auctionSmallestQuantityUnitValidator(
        t,
        Constants.MIN_SMALLEST_UNIT,
        quantityValueState.value,
        maxBidSizeState.value),
    wafValidator(t)), [t, quantityValueState.value, maxBidSizeState.value]));
    const {
        validator: maxBidSizeValidator,
        isValid: isMaxBidSizeValid,
        reset: resetMaxBidSizeValidator,
    } = useSingleValidator(true, useMemo(() => compositeValidator(auctionMaxBidSizeValidator(
        t,
        Constants.MIN_MAXIMUM_BID_SIZE,
        smallestQuantityUnitState.value,
        quantityValueState.value),
    wafValidator(t)), [t, smallestQuantityUnitState.value, quantityValueState.value]));
    const {
        validator: aucioneerDataItemsValidator,
        isValid: isAuctioneerDataItemsValid,
        reset: resetAuctioneerDataItemsValidator,
    } = useSingleValidator(true, useMemo(() => auctionAuctioneerDataItemsValidator(t, quantityValueState.value), [t, quantityValueState.value]));
    const [auctioneerDataMatrixError, setAuctioneerDataMatrixError] = useState(null);
    useEffectOnUpdate(() => {
        aucioneerDataItemsValidator(auctioneerDataItemsState.value)
            .then(() => setAuctioneerDataMatrixError(null))
            .catch((e) => setAuctioneerDataMatrixError(e));
    }, [quantityValueState.value, auctioneerDataItemsState.value]);
    const timeDiffToWarn = Constants.HOURS_DIFF_TO_WARN * 60 * 60 * 1000; // ms
    const handleNewOpeningTime = (newOpeningTime) => {
        const oldOpeningTime = openingTimeState.value;
        if (newOpeningTime !== oldOpeningTime) {
            openingTimeState.setValue(newOpeningTime);
            if (newOpeningTime instanceof Date && closingTimeState.value instanceof Date) {
                if (oldOpeningTime instanceof Date) {
                    const newClosingTime = date(newOpeningTime, (d) => {
                        const hoursDiff = Math.abs(closingTimeState.value - oldOpeningTime) / 36e5;
                        d.setHours(d.getHours() + hoursDiff);
                    });
                    closingTimeState.setValue(newClosingTime);
                    updateDurationConfirmation(newOpeningTime, newClosingTime);
                } else {
                    updateDurationConfirmation(newOpeningTime, closingTimeState.value);
                }
            }
        }
    };
    const handleNewClosingTime = (newClosingTime) => {
        if (newClosingTime !== closingTimeState.value) {
            setPublishingDisabled(newClosingTime < date());
            closingTimeState.setValue(newClosingTime);
            if (newClosingTime instanceof Date && openingTimeState.value instanceof Date) {
                updateDurationConfirmation(openingTimeState.value, newClosingTime);
            }
        }
    };
    const updateDurationConfirmation = (openingTime, closingTime) => {
        setIsShowConfirmation(Math.abs(closingTime.getTime() - openingTime.getTime()) > timeDiffToWarn);
    };
    const onProductSelect = (newValue) => {
        deliveryYearState.reset();
        quantityValueState.reset();
        powerState.reset();
        settlementStartTypeState.reset();
        fastMarketState.reset();
        const newProduct = availableProducts.find((p) => p.productId === newValue);
        productTypeState.setValue(getProductType(newProduct.productType));
        productState.setValue(newProduct);
    };
    const onProductReset = () => {
        productTypeState.reset();
        productState.reset();
    };
    const onZoneSelect = (newValue) => {
        const newZone = availableZones.find((z) => z.code === newValue);
        zoneState.setValue(newZone);
    };
    const onAuctioneerSelect = (newValue) => {
        const newAuctioneer = availableTechnicalAuctioneers.find((a) => a.memberId === newValue);
        resetAuctioneerDataItemsValidator();
        auctioneerDataItemsState.reset();
        auctioneerState.setValue(newAuctioneer);
    };
    const onBiddingTypeSelect = (newValue) => {
        showRankingState.reset();
        limitBidModificationState.reset();
        smallestQuantityUnitState.reset();
        resetSmallestQuantityUnitValidator();
        maxBidSizeState.reset();
        resetMaxBidSizeValidator();
        biddingTypeState.setValue(getBiddingType(newValue));
    };
    const onTradePriceCalculationTypeSelect = (newValue) => {
        showLastPriceState.reset();
        tradePriceCalculationTypeState.setValue(getTradePriceCalculationType(newValue));
    };
    const isChanged = nameState.isChanged || deliveryYearState.isChanged || productState.isChanged || quantityValueState.isChanged || powerState.isChanged
        || openingTimeState.isChanged || closingTimeState.isChanged || descriptionState.isChanged || minPriceState.isChanged || maxPriceState.isChanged
        || showLastPriceState.isChanged || showRankingState.isChanged || limitBidModificationState.isChanged || spotReferencePriceState.isChanged
        || absoluteDeviationState.isChanged || relativeDeviationState.isChanged || zoneState.isChanged || auctioneerState.isChanged || auctionTypeState.isChanged
        || biddingTypeState.isChanged || tradePriceCalculationTypeState.isChanged || acceptanceTypeState.isChanged || algorithmState.isChanged
        || smallestQuantityUnitState.isChanged || maxBidSizeState.isChanged || settlementStartTypeState.isChanged || fastMarketState.isChanged
        || auctioneerDataItemsState.isChanged || roundUpHighestAcceptablePriceState.isChanged || revenueSplitState.isChanged;

    const [isDisabled, setIsDisabled] = useState(true);
    const isChangedPromise = isChanged ? Promise.resolve() : Promise.reject();
    let initialValidation = Promise.resolve();
    if (auctioneerState.value.euAuctioneer) {
        initialValidation = isAuctioneerDataItemsValid();
    }
    Promise.all([initialValidation, isValid(), isMinPriceValid(), isMaxPriceValid(), isSmallestQuantityUnitValid(), isMaxBidSizeValid(), isChangedPromise])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const [isReportsPopupVisible, setReportsPopupVisible] = useState(false);
    const onReportsClickHandler = () => setReportsPopupVisible(true);
    const onReportsHideHandler = () => setReportsPopupVisible(false);
    const updateAuctionVariables = {
        id: auction.id,
        name: nameState.value,
        deliveryYear: productTypeState.value.is(ENERGY) ? parseInt(deliveryYearState.value, 10) : null,
        productId: productState.value.productId,
        quantity: quantityValueState.value,
        power: productTypeState.value.is(ENERGY) ? parseInt(powerState.value, 10) : null,
        openingTime: openingTimeState.value instanceof Date ? openingTimeState.value.toISOString() : null,
        closureTime: closingTimeState.value instanceof Date ? closingTimeState.value.toISOString() : null,
        showLastPrice: tradePriceCalculationTypeState.value.is(BID_PRICE) ? showLastPriceState.value : null,
        showRanking: biddingTypeState.value.is(SLOTTED) ? showRankingState.value : null,
        limitBidModification: biddingTypeState.value.is(SLOTTED) ? limitBidModificationState.value : null,
        minPrice: minPriceState.value,
        maxPrice: maxPriceState.value,
        description: descriptionState.value,
        spotReferencePrice: spotReferencePriceState.value,
        relativeDeviation: relativeDeviationState.value,
        absoluteDeviation: absoluteDeviationState.value,
        roundUpHighestAcceptablePrice: roundUpHighestAcceptablePriceState.value,
        zoneCode: zoneState.value.code,
        auctioneerMemberId: auctioneerState.value.memberId,
        auctionType: auctionTypeState.value,
        biddingType: biddingTypeState.value.serverType,
        tradePriceCalculationType: tradePriceCalculationTypeState.value.serverType,
        auctionAlgorithm: algorithmState.value,
        auctionAcceptanceType: acceptanceTypeState.value,
        smallestUnit: biddingTypeState.value.is(OPEN) ? smallestQuantityUnitState.value : null,
        maxBidSize: biddingTypeState.value.is(OPEN) ? maxBidSizeState.value : null,
        settlStartType: productTypeState.value.is(SECURITY) ? settlementStartTypeState.value : null,
        fastMarket: productTypeState.value.is(SECURITY) ? fastMarketState.value : null,
        auctioneerDataItems: auctioneerState.value.euAuctioneer ? auctioneerDataItemsState.value.map((item) => ({
            memberId: item.auctioneer.memberId,
            submarketCode: item.submarket.code,
            quantity: item.quantity,
        })) : null,
        revenueSplit: revenueSplitState.value,
    };
    const statesToResetOnSuccess = [
        nameState, deliveryYearState, productState, productTypeState, zoneState, auctioneerState, quantityValueState, powerState,
        openingTimeState, closingTimeState, descriptionState, minPriceState, maxPriceState, spotReferencePriceState, absoluteDeviationState,
        relativeDeviationState, showLastPriceState, showRankingState, limitBidModificationState, auctionTypeState, biddingTypeState,
        tradePriceCalculationTypeState, acceptanceTypeState, algorithmState, smallestQuantityUnitState, maxBidSizeState, settlementStartTypeState,
        fastMarketState, auctioneerDataItemsState, roundUpHighestAcceptablePriceState, revenueSplitState,
    ];
    const updateAuctionMutation = useExtendedMutation(UPDATE_AUCTION, {
        variables: updateAuctionVariables,
        successCallback: () => setResetComponentsAfterModification(true),
        statesToResetOnSuccess,
        successMessageOptions: { key: 'auctions.notification.auction-update-success', dataProperty: 'updateAuction' },
        failureMessageOptions: { key: 'auctions.notification.auction-update-failure', params: { name: auction.name } },
        refetchQueries: [ALL_AUCTIONS_QUERY_NAME, {
            query: AUCTION_DETAIL,
            variables: { auctionId: auction.id },
        }],
    });
    const publishAuctionMutation = useExtendedMutation(PUBLISH_AUCTION, {
        variables: { id: auction.id },
        successMessageOptions: { key: 'auctions.notification.auction-publish-success', dataProperty: 'publishAuction' },
        failureMessageOptions: { key: 'auctions.notification.auction-publish-failure', params: { name: auction.name } },
        refetchQueries: [ALL_AUCTIONS_QUERY_NAME, {
            query: AUCTION_DETAIL,
            variables: { auctionId: auction.id },
        }],
    });
    const openAuctionMutation = useExtendedMutation(OPEN_AUCTION, {
        variables: { id: auction.id },
        successMessageOptions: { key: 'auctions.notification.auction-open-success', dataProperty: 'openAuction' },
        failureMessageOptions: { key: 'auctions.notification.auction-open-failure', params: { name: auction.name } },
        refetchQueries: [ALL_AUCTIONS_QUERY_NAME, {
            query: AUCTION_DETAIL,
            variables: { auctionId: auction.id },
        }],
    });
    const closeAuctionMutation = useExtendedMutation(CLOSE_AUCTION, {
        variables: { id: auction.id },
        successMessageOptions: { key: 'auctions.notification.auction-close-success', dataProperty: 'closeAuction' },
        failureMessageOptions: { key: 'auctions.notification.auction-close-failure', params: { name: auction.name } },
        refetchQueries: [ALL_AUCTIONS_QUERY_NAME, {
            query: AUCTION_DETAIL,
            variables: { auctionId: auction.id },
        }],
    });
    const acceptAuctionMutation = useExtendedMutation(ACCEPT_AUCTION, {
        variables: { id: auction.id },
        successMessageOptions: { key: 'auctions.notification.auction-accept-success', dataProperty: 'acceptAuction' },
        failureMessageOptions: { key: 'auctions.notification.auction-accept-failure', params: { name: auction.name } },
        refetchQueries: [ALL_AUCTIONS_QUERY_NAME, {
            query: AUCTION_DETAIL,
            variables: { auctionId: auction.id },
        }],
    });
    const rejectAuctionMutation = useExtendedMutation(REJECT_AUCTION, {
        variables: { id: auction.id },
        successMessageOptions: { key: 'auctions.notification.auction-cancel-success', dataProperty: 'rejectAuction' },
        failureMessageOptions: { key: 'auctions.notification.auction-cancel-failure', params: { name: auction.name } },
        refetchQueries: [ALL_AUCTIONS_QUERY_NAME, {
            query: AUCTION_DETAIL,
            variables: { auctionId: auction.id },
        }],
    });
    const cancelAuctionMutation = useExtendedMutation(CANCEL_AUCTION, {
        variables: { id: auction.id },
        successMessageOptions: { key: 'auctions.notification.auction-delete-success', dataProperty: 'cancelAuction' },
        failureMessageOptions: { key: 'auctions.notification.auction-delete-failure', params: { name: auction.name } },
        refetchQueries: [ALL_AUCTIONS_QUERY_NAME, {
            query: AUCTION_DETAIL,
            variables: { auctionId: auction.id },
        }],
    });
    const uiPhase = getUIPhase(auction.phase);
    const editPrivileges = [Privileges.AUCTION_EDIT];
    const readAllPrivileges = [Privileges.AUCTION_DETAIL_VIEW_ALL];
    const readConfigurationPartsPrivileges = [].concat(readAllPrivileges, [Privileges.AUCTION_VIEW_ALL, Privileges.AUCTION_VIEW_OWN]);
    const readMinMaxPricePrivileges = [].concat(readAllPrivileges, [Privileges.AUCTION_VIEW_ALL]);
    const canFieldsBeModified = uiPhase.is(SUBMITTED) || uiPhase.is(PUBLISHED);
    const canFieldBeModifiedInRunningAuction = canFieldsBeModified || uiPhase.is(RUNNING);
    const canFieldBeModifiedInRunningAndClosedAuction = canFieldBeModifiedInRunningAuction || uiPhase.is(CLOSED);
    const sufficientPrivilegesFields = canFieldsBeModified ? editPrivileges : [];
    const sufficientPrivilegesInRunningAuction = canFieldBeModifiedInRunningAuction ? editPrivileges : [];
    const sufficientPrivilegesInRunningAndClosedAuction = canFieldBeModifiedInRunningAndClosedAuction ? editPrivileges : [];
    let isDownloadReportsVisible = false;
    if (productTypeState.value.is(SECURITY)) {
        isDownloadReportsVisible = true;
    } else if (productTypeState.value.is(ENERGY)) {
        isDownloadReportsVisible = uiPhase.is(FINISHED);
    }
    const lowestAcceptablePrice = hasPrivilege(userPrivileges, sufficientPrivilegesInRunningAndClosedAuction)
        ? computeLowestAcceptablePrice(
            spotReferencePriceState.value,
            relativeDeviationState.value,
            absoluteDeviationState.value,
        )
        : auction.lowestAcceptablePrice;
    const highestAcceptablePrice = hasPrivilege(userPrivileges, sufficientPrivilegesInRunningAndClosedAuction)
        ? computeHighestAcceptablePrice(
            spotReferencePriceState.value,
            relativeDeviationState.value,
            absoluteDeviationState.value,
            roundUpHighestAcceptablePriceState.value,
        )
        : auction.highestAcceptablePrice;
    const canSeeAuctionConfiguration = hasPrivilege(userPrivileges, [].concat(editPrivileges, readConfigurationPartsPrivileges));
    const anyDropdownVisible = hasPrivilege(userPrivileges, readConfigurationPartsPrivileges) && (
        availableAuctionTypes.length > 1
        || availableBiddingTypes.length > 1
        || availableTradePriceCalculationTypes.length > 1
        || availableAuctionAlgorithms.length > 1
        || availableAuctionAcceptanceTypes.length > 1
    );
    const minMaxPriceVisible = hasPrivilege(userPrivileges, minPriceState.value === null ? readAllPrivileges : readMinMaxPricePrivileges);
    const otherConfigurationVisible = hasPrivilege(userPrivileges, [].concat(editPrivileges, readAllPrivileges));
    const lowestAcceptablePriceVisible = (getAuctionType(auctionTypeState.value).is(BUY) || (getAuctionType(auctionTypeState.value).is(SELL) && getAuctionAcceptanceType(acceptanceTypeState.value).is(AUTO))) && lowestAcceptablePrice !== null;
    const highestAcceptablePriceVisible = ((getAuctionType(auctionTypeState.value).is(BUY) && getAuctionAcceptanceType(acceptanceTypeState.value).is(AUTO)) || getAuctionType(auctionTypeState.value).is(SELL)) && highestAcceptablePrice !== null;
    const shouldShowAuctionConfiguration = canSeeAuctionConfiguration && (
        anyDropdownVisible
        || minMaxPriceVisible
        || otherConfigurationVisible
        || lowestAcceptablePriceVisible
        || highestAcceptablePriceVisible
        || productTypeState.value.is(SECURITY)
        || biddingTypeState.value.is(OPEN)
    );
    return (
        <ExpandedListTab
            className="auction-detail-tab"
        >
            <Collapsible
                header={<h5>{t('auctions.auction-setup.auction-details')}</h5>}
            >
                <div className="auction-setup--details">
                    <PrivilegedTextField
                        sufficientPrivileges={sufficientPrivilegesFields}
                        label={t('auctions.auction-setup.name')}
                        state={nameState}
                        className="auction-detail-tab--fields--name wide"
                        inputId="auction-detail-tab--fields--name"
                        validator={validators.name}
                        maxLength={AUCTION_NAME_LENGTH}
                        isRequired
                    />
                    <PrivilegedSelectField
                        sufficientPrivileges={sufficientPrivilegesFields}
                        label={t('auctions.auction-setup.product')}
                        onChange={onProductSelect}
                        className="auction-detail-tab--fields--product"
                        inputId="auction-detail-tab--fields--product"
                        state={productState}
                        value={productState.value.productId}
                        translatedReadonlyValue={`${productState.value.name} (${productState.value.productId})`}
                        options={availableProducts.map((p) => <option value={p.productId} key={p.productId}>{p.name} ({p.productId})</option>)}
                        onReset={onProductReset}
                        isRequired
                    />
                    {productTypeState.value.is(SECURITY) && (
                        <ReadonlyField
                            label={t('auctions.auction-setup.scope')}
                            value={auction.scope ? getScope(auction.scope).translate(t) : ''}
                            className="auction-detail-tab--fields--scope"
                        />
                    )}
                    {availableZones.length > 1 && (
                        <PrivilegedSelectField
                            sufficientPrivileges={sufficientPrivilegesFields}
                            label={t('auctions.auction-setup.zone')}
                            onChange={onZoneSelect}
                            className="auction-detail-tab--fields--zone"
                            inputId="auction-detail-tab--fields--zone"
                            state={zoneState}
                            value={zoneState.value.code}
                            translatedReadonlyValue={`${zoneState.value.name} (${zoneState.value.code})`}
                            options={availableZones.map((z) => <option value={z.code} key={z.code}>{z.name} ({z.code})</option>)}
                            isRequired
                        />
                    )}
                    {availableTechnicalAuctioneers.length > 1 && (
                        <PrivilegedSelectField
                            sufficientPrivileges={sufficientPrivilegesFields}
                            label={t('auctions.auction-setup.auctioneer')}
                            onChange={onAuctioneerSelect}
                            className="auction-detail-tab--fields--auctioneer"
                            inputId="auction-detail-tab--fields--auctioneer"
                            state={auctioneerState}
                            value={auctioneerState.value.memberId}
                            translatedReadonlyValue={`${auctioneerState.value.name} (${auctioneerState.value.memberId})`}
                            options={availableTechnicalAuctioneers.map((a) => <option value={a.memberId} key={a.memberId}>{a.name} ({a.memberId})</option>)}
                            isRequired
                        />
                    )}
                    {productTypeState.value.is(SECURITY) && (
                        <Privileged sufficientPrivileges={editPrivileges}>
                            <PrivilegedNumberField
                                sufficientPrivileges={sufficientPrivilegesFields}
                                label={t('auctions.auction-setup.revenue-split')}
                                className="auction-detail-tab--fields--revenue-split"
                                inputId="auction-detail-tab--fields--revenue-split"
                                state={revenueSplitState}
                                isRequired={true}
                                min={Constants.MIN_REVENUE_SPLIT}
                                max={Constants.MAX_REVENUE_SPLIT}
                                fractionDigits={0}
                                validator={validators.revenueSplit}
                                postfix={<>&nbsp;%</>}
                                step={1}
                            />
                        </Privileged>
                    )}
                    <PrivilegedDateTimeField
                        sufficientPrivileges={sufficientPrivilegesFields}
                        label={t('auctions.auction-setup.opening')}
                        state={openingTimeState}
                        onChange={handleNewOpeningTime}
                        className="auction-detail-tab--fields--opening-time wide"
                        inputId="auction-detail-tab--fields--opening-time"
                        invalidBefore={date()}
                        validator={validators.openingTime}
                        validateOnLoad
                        isRequired
                        keepPosition
                    />
                    <PrivilegedDateTimeField
                        sufficientPrivileges={sufficientPrivilegesInRunningAuction}
                        label={t('auctions.auction-setup.closing')}
                        state={closingTimeState}
                        onChange={handleNewClosingTime}
                        className="auction-detail-tab--fields--closing-time wide"
                        inputId="auction-detail-tab--fields--closing-time"
                        invalidBefore={openingTimeState.value}
                        validator={validators.closingTime}
                        validateOnLoad
                        isRequired
                        keepPosition
                    />
                    {productTypeState.value.is(ENERGY) && (
                        <>
                            <PrivilegedNumberField
                                sufficientPrivileges={sufficientPrivilegesFields}
                                label={t('auctions.auction-setup.delivery-year')}
                                className="auction-detail-tab--fields--delivery-year"
                                inputId="auction-detail-tab--fields--delivery-year"
                                state={deliveryYearState}
                                min={getCurrentYear()}
                                validator={validators.deliveryYear}
                                noThousandSeparator
                                isRequired
                            />
                            <PrivilegedNumberField
                                sufficientPrivileges={sufficientPrivilegesFields}
                                label={t('auctions.auction-setup.number-of-lots')}
                                className="auction-detail-tab--fields--quantity"
                                inputId="auction-detail-tab--fields--quantity"
                                state={quantityValueState}
                                validator={validators.quantityValue}
                                min={1}
                                step={Constants.DEFAULT_QUANTITY_STEP}
                                isRequired
                            />
                            <PrivilegedNumberField
                                sufficientPrivileges={sufficientPrivilegesFields}
                                label={t('auctions.auction-setup.power')}
                                className="auction-detail-tab--fields--power"
                                inputId="auction-detail-tab--fields--power"
                                state={powerState}
                                validator={validators.power}
                                postfix={<Unit unit={quantityUnit.substring(0, quantityUnit.length - 1)} />}
                                min={1}
                                isRequired
                            />
                            <PrivilegedAuctionEnergyPerLot
                                sufficientPrivileges={sufficientPrivilegesFields}
                                energyPerLot={auction.energyPerLot}
                                label={t('auctions.auction-setup.energy-per-lot')}
                                deliveryYear={debouncedDeliveryYear ? String(debouncedDeliveryYear) : ''}
                                productId={productState.value.productId}
                                quantityUnit={<Unit unit={quantityUnit} />}
                                power={debouncedPower ? String(debouncedPower) : ''}
                                className="auction-detail-tab--fields--energy-per-lot"
                            />
                        </>
                    )}
                    {productTypeState.value.is(SECURITY) && (
                        <PrivilegedNumberField
                            sufficientPrivileges={sufficientPrivilegesFields}
                            label={t('auctions.auction-setup.quantity')}
                            className="auction-detail-tab--fields--quantity"
                            inputId="auction-detail-tab--fields--quantity"
                            state={quantityValueState}
                            validator={validators.quantityValue}
                            min={1}
                            step={Constants.DEFAULT_QUANTITY_STEP}
                            isRequired
                        />
                    )}
                    <ReadonlyField
                        label={t('auctions.auction-setup.phase')}
                        value={uiPhase.translate(t)}
                        className="auction-detail-tab--fields--phase"
                    />
                    <PrivilegedTextField
                        sufficientPrivileges={sufficientPrivilegesFields}
                        label={t('auctions.auction-setup.description')}
                        state={descriptionState}
                        emptyReadonlyValue={t('auctions.auction-setup.no-description')}
                        className="auction-detail-tab--fields--description x-wide"
                        inputId="auction-detail-tab--fields--description"
                        validator={validators.description}
                        maxLength={AUCTION_DESCRIPTION_MAX_LENGTH}
                        keepPosition
                    />
                </div>
            </Collapsible>
            {shouldShowAuctionConfiguration && (
                <Collapsible
                    header={<h5>{t('auctions.auction-setup.auction-configuration')}</h5>}
                >
                    <div className="auction-configuration">
                        {productTypeState.value.is(ENERGY) && (
                            <PrivilegedCheckboxField
                                sufficientPrivileges={sufficientPrivilegesInRunningAndClosedAuction}
                                sufficientReadPrivileges={readAllPrivileges}
                                label={t('auctions.auction-setup.round-up-highest-acceptable-price')}
                                state={roundUpHighestAcceptablePriceState}
                                className="auction-detail-tab--fields--round-up-highest-acceptable-price"
                                inputId="auction-detail-tab--fields--round-up-highest-acceptable-price"
                            />
                        )}
                        {availableAuctionTypes.length > 1 && (
                            <PrivilegedSelectField
                                sufficientPrivileges={sufficientPrivilegesFields}
                                sufficientReadPrivileges={readConfigurationPartsPrivileges}
                                label={t('auctions.auction-setup.auction-type')}
                                state={auctionTypeState}
                                className="auction-detail-tab--fields--auction-type"
                                inputId="auction-detail-tab--fields--auction-type"
                                translatedReadonlyValue={getAuctionType(auctionTypeState.value).translate(t)}
                                options={availableAuctionTypes.map((availableAuctionType) => (
                                    <option value={availableAuctionType} key={availableAuctionType}>
                                        {getAuctionType(availableAuctionType).translate(t)}
                                    </option>
                                ))}
                                isRequired
                            />
                        )}
                        {availableBiddingTypes.length > 1 && (
                            <PrivilegedSelectField
                                sufficientPrivileges={sufficientPrivilegesFields}
                                sufficientReadPrivileges={readConfigurationPartsPrivileges}
                                label={t('auctions.auction-setup.bidding-type')}
                                onChange={onBiddingTypeSelect}
                                className="auction-detail-tab--fields--bidding-type"
                                inputId="auction-detail-tab--fields--bidding-type"
                                state={biddingTypeState}
                                value={biddingTypeState.value.serverType}
                                translatedReadonlyValue={biddingTypeState.value.translate(t)}
                                options={availableBiddingTypes.map((availableBiddingType) => (
                                    <option value={availableBiddingType} key={availableBiddingType}>
                                        {getBiddingType(availableBiddingType).translate(t)}
                                    </option>
                                ))}
                                isRequired
                            />
                        )}
                        {availableTradePriceCalculationTypes.length > 1 && (
                            <PrivilegedSelectField
                                sufficientPrivileges={sufficientPrivilegesFields}
                                sufficientReadPrivileges={readConfigurationPartsPrivileges}
                                label={t('auctions.auction-setup.trade-price-calculation-type')}
                                onChange={onTradePriceCalculationTypeSelect}
                                className="auction-detail-tab--fields--trade-price-calculation-type"
                                inputId="auction-detail-tab--fields--trade-price-calculation-type"
                                state={tradePriceCalculationTypeState}
                                value={tradePriceCalculationTypeState.value.serverType}
                                translatedReadonlyValue={tradePriceCalculationTypeState.value.translate(t)}
                                options={availableTradePriceCalculationTypes.map((availableTradePriceCalculationType) => (
                                    <option value={availableTradePriceCalculationType} key={availableTradePriceCalculationType}>
                                        {getTradePriceCalculationType(availableTradePriceCalculationType).translate(t)}
                                    </option>
                                ))}
                                isRequired
                            />
                        )}
                        {availableAuctionAlgorithms.length > 1 && (
                            <PrivilegedSelectField
                                sufficientPrivileges={sufficientPrivilegesFields}
                                sufficientReadPrivileges={readConfigurationPartsPrivileges}
                                label={t('auctions.auction-setup.algorithm')}
                                state={algorithmState}
                                className="auction-detail-tab--fields--algorithm"
                                inputId="auction-detail-tab--fields--algorithm"
                                translatedReadonlyValue={getAuctionAlgorithm(algorithmState.value).translate(t)}
                                options={availableAuctionAlgorithms.map((availableAuctionAlgorithm) => (
                                    <option value={availableAuctionAlgorithm} key={availableAuctionAlgorithm}>
                                        {getAuctionAlgorithm(availableAuctionAlgorithm).translate(t)}
                                    </option>
                                ))}
                                isRequired
                            />
                        )}
                        {availableAuctionAcceptanceTypes.length > 1 && (
                            <PrivilegedSelectField
                                sufficientPrivileges={sufficientPrivilegesFields}
                                sufficientReadPrivileges={readConfigurationPartsPrivileges}
                                label={t('auctions.auction-setup.acceptance-type')}
                                state={acceptanceTypeState}
                                className="auction-detail-tab--fields--acceptance-type"
                                inputId="auction-detail-tab--fields--acceptance-type"
                                translatedReadonlyValue={getAuctionAcceptanceType(acceptanceTypeState.value).translate(t)}
                                options={availableAuctionAcceptanceTypes.map((availableAcceptanceType) => (
                                    <option value={availableAcceptanceType} key={availableAcceptanceType}>
                                        {getAuctionAcceptanceType(availableAcceptanceType).translate(t)}
                                    </option>
                                ))}
                                isRequired
                            />
                        )}
                        {tradePriceCalculationTypeState.value.is(BID_PRICE) && (
                            <PrivilegedCheckboxField
                                sufficientPrivileges={sufficientPrivilegesFields}
                                sufficientReadPrivileges={readAllPrivileges}
                                label={t('auctions.auction-setup.show-last-price')}
                                state={showLastPriceState}
                                className="auction-detail-tab--fields--show-last-price"
                                inputId="auction-detail-tab--fields--show-last-price"
                            />
                        )}
                        {biddingTypeState.value.is(SLOTTED) && (
                            <>
                                <PrivilegedCheckboxField
                                    sufficientPrivileges={sufficientPrivilegesFields}
                                    sufficientReadPrivileges={readAllPrivileges}
                                    label={t('auctions.auction-setup.show-ranking')}
                                    state={showRankingState}
                                    className="auction-detail-tab--fields--show-ranking"
                                    inputId="auction-detail-tab--fields--show-ranking"
                                />
                                <PrivilegedCheckboxField
                                    sufficientPrivileges={sufficientPrivilegesFields}
                                    sufficientReadPrivileges={readAllPrivileges}
                                    label={t('auctions.auction-setup.limit-bid-modification')}
                                    state={limitBidModificationState}
                                    className="auction-detail-tab--fields--limit-bid-modification"
                                    inputId="auction-detail-tab--fields--limit-bid-modification"
                                />
                            </>
                        )}
                        <PrivilegedNumberField
                            sufficientPrivileges={sufficientPrivilegesFields}
                            sufficientReadPrivileges={minPriceState.value === null ? readAllPrivileges : readMinMaxPricePrivileges}
                            label={t('auctions.auction-setup.min-price')}
                            className="auction-detail-tab--fields--min-price"
                            inputId="auction-detail-tab--fields--min-price"
                            state={minPriceState}
                            emptyReadonlyValue={t('auctions.auction-setup.no-min-price')}
                            max={maxPriceState.value}
                            fractionDigits={2}
                            validator={minPriceValidator}
                            postfix={currencyPostfix}
                            step={Constants.DEFAULT_PRICE_STEP}
                            validateOnValidatorChange
                        />
                        <PrivilegedNumberField
                            sufficientPrivileges={sufficientPrivilegesFields}
                            sufficientReadPrivileges={maxPriceState.value === null ? readAllPrivileges : readMinMaxPricePrivileges}
                            label={t('auctions.auction-setup.max-price')}
                            className="auction-detail-tab--fields--max-price"
                            inputId="auction-detail-tab--fields--max-price"
                            state={maxPriceState}
                            emptyReadonlyValue={t('auctions.auction-setup.no-max-price')}
                            min={minPriceState.value}
                            fractionDigits={2}
                            validator={maxPriceValidator}
                            postfix={currencyPostfix}
                            step={Constants.DEFAULT_PRICE_STEP}
                            validateOnValidatorChange
                        />
                        <PrivilegedNumberField
                            sufficientPrivileges={sufficientPrivilegesInRunningAndClosedAuction}
                            sufficientReadPrivileges={readAllPrivileges}
                            label={t('auctions.auction-setup.spot-reference-price')}
                            className="auction-detail-tab--fields--spot-reference-price"
                            inputId="auction-detail-tab--fields--spot-reference-price"
                            state={spotReferencePriceState}
                            emptyReadonlyValue={t('auctions.auction-setup.no-spot-reference-price')}
                            min={Constants.MIN_SPOT_REFERENCE_PRICE}
                            max={Constants.MAX_SPOT_REFERENCE_PRICE}
                            fractionDigits={2}
                            validator={validators.spotReferencePrice}
                            postfix={currencyPostfix}
                            step={Constants.DEFAULT_PRICE_STEP}
                        />
                        <PrivilegedNumberField
                            sufficientPrivileges={sufficientPrivilegesInRunningAndClosedAuction}
                            sufficientReadPrivileges={readAllPrivileges}
                            label={t('auctions.auction-setup.abs-deviation-limit')}
                            className="auction-detail-tab--fields--absolute-deviation"
                            inputId="auction-detail-tab--fields--absolute-deviation"
                            state={absoluteDeviationState}
                            emptyReadonlyValue={t('auctions.auction-setup.no-abs-deviation-limit')}
                            min={Constants.MIN_ABSOLUTE_DEVIATION}
                            max={Constants.MAX_ABSOLUTE_DEVIATION}
                            fractionDigits={2}
                            validator={validators.absoluteDeviation}
                            postfix={currencyPostfix}
                            step={Constants.DEFAULT_PRICE_STEP}
                        />
                        <PrivilegedNumberField
                            sufficientPrivileges={sufficientPrivilegesInRunningAndClosedAuction}
                            sufficientReadPrivileges={readAllPrivileges}
                            label={t('auctions.auction-setup.rel-deviation-limit')}
                            className="auction-detail-tab--fields--relative-deviation"
                            inputId="auction-detail-tab--fields--relative-deviation"
                            state={relativeDeviationState}
                            emptyReadonlyValue={t('auctions.auction-setup.no-rel-deviation-limit')}
                            min={Constants.MIN_RELATIVE_DEVIATION}
                            max={Constants.MAX_RELATIVE_DEVIATION}
                            fractionDigits={2}
                            validator={validators.relativeDeviation}
                            postfix={<>&nbsp;%</>}
                            step={Constants.DEFAULT_PRICE_STEP}
                        />
                        {productTypeState.value.is(SECURITY) && (
                            <>
                                <PrivilegedSelectField
                                    sufficientPrivileges={sufficientPrivilegesFields}
                                    sufficientReadPrivileges={readConfigurationPartsPrivileges}
                                    label={t('auctions.auction-setup.settlement-start')}
                                    state={settlementStartTypeState}
                                    className="auction-detail-tab--fields--settlement-start"
                                    inputId="auction-detail-tab--fields--settlement-start"
                                    translatedReadonlyValue={getSettlementStartType(settlementStartTypeState.value).translate(t)}
                                    options={availableSettlStartTypes.map((availableSettlementStartType) => (
                                        <option value={availableSettlementStartType} key={availableSettlementStartType}>
                                            {getSettlementStartType(availableSettlementStartType).translate(t)}
                                        </option>
                                    ))}
                                    isRequired
                                />
                                <PrivilegedCheckboxField
                                    sufficientPrivileges={sufficientPrivilegesInRunningAndClosedAuction}
                                    sufficientReadPrivileges={readAllPrivileges}
                                    label={t('auctions.auction-setup.fast-market')}
                                    state={fastMarketState}
                                    className="auction-detail-tab--fields--fast-market"
                                    inputId="auction-detail-tab--fields--fast-market"
                                />
                            </>
                        )}
                        {biddingTypeState.value.is(OPEN) && (
                            <>
                                <PrivilegedNumberField
                                    sufficientPrivileges={sufficientPrivilegesFields}
                                    sufficientReadPrivileges={readConfigurationPartsPrivileges}
                                    label={t('auctions.auction-setup.smallest-unit')}
                                    className="auction-detail-tab--fields--smallest-quantity-unit"
                                    inputId="auction-detail-tab--fields--smallest-quantity-unit"
                                    state={smallestQuantityUnitState}
                                    min={Constants.MIN_SMALLEST_UNIT}
                                    max={quantityValueState.value}
                                    validator={smallestQuantityUnitValidator}
                                    step={Constants.DEFAULT_QUANTITY_STEP}
                                    isRequired
                                    validateOnValidatorChange
                                    validateOnLoad
                                />
                                <PrivilegedNumberField
                                    sufficientPrivileges={sufficientPrivilegesFields}
                                    sufficientReadPrivileges={readConfigurationPartsPrivileges}
                                    label={t('auctions.auction-setup.maximum-bid-size')}
                                    className="auction-detail-tab--fields--max-bid-size"
                                    inputId="auction-detail-tab--fields--max-bid-size"
                                    state={maxBidSizeState}
                                    emptyReadonlyValue={t('auctions.auction-setup.no-limit')}
                                    min={Constants.MIN_MAXIMUM_BID_SIZE}
                                    max={quantityValueState.value}
                                    validator={maxBidSizeValidator}
                                    step={smallestQuantityUnitState.value}
                                    validateOnValidatorChange
                                    validateOnLoad
                                />
                            </>
                        )}
                        {lowestAcceptablePriceVisible && (
                            <ReadonlyNumberField
                                label={t('auctions.auction-setup.lowest-acceptable-price')}
                                value={lowestAcceptablePrice}
                                fractionDigits={2}
                                className="auction-detail-tab--fields--lowest-acceptable-price"
                                postfix={currencyPostfix}
                            />
                        )}
                        {highestAcceptablePriceVisible && (
                            <ReadonlyNumberField
                                label={t('auctions.auction-setup.highest-acceptable-price')}
                                value={highestAcceptablePrice}
                                fractionDigits={2}
                                className="auction-detail-tab--fields--highest-acceptable-price"
                                postfix={currencyPostfix}
                            />
                        )}
                    </div>
                </Collapsible>
            )}
            <Privileged sufficientPrivileges={[].concat(editPrivileges, readConfigurationPartsPrivileges)}>
                {auctioneerState.value.euAuctioneer && (
                    <Collapsible
                        header={<h5>{t('auctioneer-data-matrix.title')}</h5>}
                        className="collapsible--auctioneer-data-matrix"
                        error={auctioneerDataMatrixError}
                        initiallyCollapsed
                    >
                        <div className="auction-detail-tab--fields--auctioneer-data-matrix">
                            <AuctioneerDataMatrix
                                sufficientPrivileges={sufficientPrivilegesInRunningAndClosedAuction}
                                availableAuctioneers={availableRegularAuctioneers}
                                availableSubmarkets={availableSubmarkets}
                                availableTemplates={availableAuctioneerDataTemplates}
                                maxQuantitySum={quantityValueState.value}
                                items={auctioneerDataItemsState.value}
                                quantityMultiple={smallestQuantityUnitState.value}
                                validator={aucioneerDataItemsValidator}
                                onItemsChange={onItemsChange}
                                onReset={auctioneerDataItemsState.reset}
                                isChanged={auctioneerDataItemsState.isChanged}
                                doResetComponentsAfterModification={resetComponentsAfterModification}
                                isRequired
                                validateOnLoad
                            />
                        </div>
                    </Collapsible>
                )}
            </Privileged>
            <AuctionResult auction={auction} />
            {(hasPrivilege(userPrivileges, editPrivileges) || isDownloadReportsVisible) && (
                <>
                    <div className="rich-list-row-expanded--buttons">
                        <div className="rich-list-row-expanded--buttons--left">
                            <Privileged sufficientPrivileges={editPrivileges}>
                                {canFieldBeModifiedInRunningAndClosedAuction && (
                                    <ApplyChangesButton
                                        extendedMutation={updateAuctionMutation}
                                        disabled={isDisabled}
                                        confirmationMessage={t('auctions.confirm-update-auction', { name: auction.name }) + (isShowConfirmation ? ' ' + t('auctions.confirm-edit-auction-hours', { hours: Constants.HOURS_DIFF_TO_WARN }) : '')}
                                        extraConfirmationMessage={t('auctions.extra-confirm-update-auction', { name: auction.name })}
                                        showConfirmationDialog={isShowConfirmation}
                                    />
                                )}
                            </Privileged>
                            {isDownloadReportsVisible && (
                                <button
                                    className="button button-bordered download-reports-button"
                                    onClick={onReportsClickHandler}
                                    onKeyUp={onActionKey(onReportsClickHandler)}
                                    type="button"
                                >
                                    <FontAwesomeIcon icon="newspaper" />&nbsp;{t('auctions.auction-setup.button.download-reports')}
                                </button>
                            )}
                        </div>
                        <Privileged sufficientPrivileges={editPrivileges}>
                            <div className="rich-list-row-expanded--buttons--middle">
                                <AuctionTransitionButton
                                    fromPhase={uiPhase}
                                    extendedMutation={publishAuctionMutation}
                                    confirmationMessage={t('auctions.confirm-publish-auction', { name: auction.name })}
                                    toPhase={PUBLISHED}
                                    className="auction-detail-tab--publish-auction"
                                    disabled={isPublishingDisabled || isChanged}
                                />
                                <AuctionTransitionButton
                                    fromPhase={uiPhase}
                                    extendedMutation={openAuctionMutation}
                                    confirmationMessage={t('auctions.confirm-open-auction', { name: auction.name })}
                                    toPhase={RUNNING}
                                    className="auction-detail-tab--open-auction"
                                    disabled={isChanged}
                                />
                                <AuctionTransitionButton
                                    fromPhase={uiPhase}
                                    extendedMutation={closeAuctionMutation}
                                    confirmationMessage={t('auctions.confirm-close-auction', { name: auction.name })}
                                    extraConfirmationMessage={t('auctions.extra-confirm-close-auction', { name: auction.name })}
                                    toPhase={CLOSED}
                                    className="auction-detail-tab--close-auction"
                                    disabled={isChanged}
                                />
                                <AuctionTransitionButton
                                    fromPhase={uiPhase}
                                    extendedMutation={acceptAuctionMutation}
                                    confirmationMessage={
                                        spotReferencePriceState.value
                                            ? t('auctions.confirm-accept-auction', { name: auction.name })
                                            : t('auctions.confirm-accept-auction-no-reference-price', { name: auction.name })
                                    }
                                    toPhase={FINISHED}
                                    className="auction-detail-tab--accept-auction"
                                    disabled={auction.acceptanceBlockers?.length > 0 || isChanged}
                                />
                                <AuctionReopenButton
                                    auction={auction}
                                    disabled={isChanged}
                                />
                                <AuctionTransitionButton
                                    fromPhase={uiPhase}
                                    extendedMutation={rejectAuctionMutation}
                                    confirmationMessage={t('auctions.confirm-cancel-auction', { name: auction.name })}
                                    toPhase={CANCELLED}
                                    className="auction-detail-tab--cancel-auction"
                                    disabled={isChanged}
                                />
                                <AuctionDistributeVolumeButton
                                    auction={auction}
                                    step={volumeDistributionStep}
                                    disabled={isChanged}
                                />
                            </div>
                            <div className="rich-list-row-expanded--buttons--right">
                                <AuctionTransitionButton
                                    fromPhase={uiPhase}
                                    extendedMutation={cancelAuctionMutation}
                                    confirmationMessage={t('auctions.confirm-delete-auction', { name: auction.name })}
                                    toPhase={DELETED}
                                    className="auction-detail-tab--delete-auction"
                                    disabled={isChanged}
                                />
                            </div>
                        </Privileged>
                    </div>
                    <Privileged sufficientPrivileges={editPrivileges}>
                        {auction.modificationBlockers?.map((modificationBlocker) => (
                            <InfoPanel
                                key={modificationBlocker}
                                message={transformAuctionBlockers(t, modificationBlocker)}
                            />
                        ))}
                        {auction.acceptanceBlockers?.map((acceptanceBlocker) => (
                            <InfoPanel
                                key={acceptanceBlocker}
                                message={transformAuctionBlockers(t, acceptanceBlocker)}
                            />
                        ))}
                        {isChanged && (
                            <InfoPanel
                                key={AUCTION_BLOCKER_MODIFIED}
                                message={transformAuctionBlockers(t, AUCTION_BLOCKER_MODIFIED)}
                            />
                        )}
                    </Privileged>
                </>
            )}
            {isReportsPopupVisible && <AuctionReportsMenu onCancel={onReportsHideHandler} auctionId={auction.id} />}
            {!canFieldsBeModified && (
                <ShowBidsPopupButton
                    auctionId={auction.id}
                    auctionProps={auctionProps}
                />
            )}
        </ExpandedListTab>
    );
}

export default AuctionDetailTab;
